import { Injectable } from '@angular/core';
import { delay, Observable, of } from 'rxjs';
import { ApiResponse } from '../../macro-posto/models';
import {
  HabilitacaoSwapModel,
  SocioModel,
  HistoricoSwapModel,
} from '../models';

@Injectable({
  providedIn: 'root',
})
export class HabilitacaoSwapService {
  // ==================== Mock Data ====================
  private mockSocios: SocioModel[] = [
    new SocioModel(1, 'João da Silva', '12345678901', 'CPF', '1985-03-15', 'Carlos da Silva', 'Maria da Silva', null, null, null),
    new SocioModel(2, 'Empresa ABC Ltda', 'AB123456000199', 'CNPJ', null, null, null, 'Empresa ABC Ltda', 'ABC', '2010-06-20'),
  ];

  private mockHistorico: HistoricoSwapModel[] = [
    new HistoricoSwapModel(1, '2026-02-20T10:30:00', 'Cadastro de sócio João da Silva realizado.', 'admin.user', 'INFO'),
    new HistoricoSwapModel(2, '2026-02-20T10:35:00', 'Cadastro de sócio Empresa ABC Ltda realizado.', 'admin.user', 'INFO'),
    new HistoricoSwapModel(3, '2026-02-21T14:00:00', 'Dados submetidos para validação SWAP/Bacen.', 'admin.user', 'SUBMISSAO'),
    new HistoricoSwapModel(4, '2026-02-22T09:15:00', 'SWAP solicitou ajuste: Data de nascimento divergente para o sócio João da Silva.', 'sistema', 'AJUSTE'),
  ];

  private mockStatus: 'NAO_INICIADO' | 'EM_PREENCHIMENTO' | 'SUBMETIDO' | 'AJUSTE_SOLICITADO' | 'APROVADO' = 'AJUSTE_SOLICITADO';
  private nextId = 3;

  // ==================== Consultar Habilitação ====================

  buscarHabilitacao(): Observable<HabilitacaoSwapModel | null> {
    const habilitacao = new HabilitacaoSwapModel(
      1,
      this.mockStatus,
      [...this.mockSocios],
      [...this.mockHistorico],
      this.mockStatus === 'APROVADO' ? '2026-02-25T08:00:00' : null,
      '2026-02-22T09:15:00',
      this.mockStatus === 'AJUSTE_SOLICITADO' ? 'Data de nascimento divergente para o sócio João da Silva.' : null,
    );
    return of(habilitacao).pipe(delay(500));
  }

  // ==================== Sócios ====================

  buscarSocios(): Observable<SocioModel[]> {
    return of([...this.mockSocios]).pipe(delay(300));
  }

  salvarSocio(socio: SocioModel): Observable<ApiResponse<any>> {
    socio.id = this.nextId++;
    this.mockSocios.push(socio);
    this.mockHistorico.unshift(
      new HistoricoSwapModel(
        this.mockHistorico.length + 1,
        new Date().toISOString(),
        `Cadastro de sócio ${socio.nome} realizado.`,
        'usuario.logado',
        'INFO',
      )
    );
    if (this.mockStatus === 'NAO_INICIADO') {
      this.mockStatus = 'EM_PREENCHIMENTO';
    }
    return of({ sucesso: true, retorno: socio.toJson(), mensagens: [] }).pipe(delay(400));
  }

  editarSocio(socio: SocioModel): Observable<ApiResponse<any>> {
    const index = this.mockSocios.findIndex(s => s.id === socio.id);
    if (index !== -1) {
      this.mockSocios[index] = socio;
      this.mockHistorico.unshift(
        new HistoricoSwapModel(
          this.mockHistorico.length + 1,
          new Date().toISOString(),
          `Sócio ${socio.nome} atualizado.`,
          'usuario.logado',
          'INFO',
        )
      );
    }
    return of({ sucesso: true, retorno: socio.toJson(), mensagens: [] }).pipe(delay(400));
  }

  removerSocio(idSocio: number): Observable<ApiResponse<any>> {
    const socio = this.mockSocios.find(s => s.id === idSocio);
    this.mockSocios = this.mockSocios.filter(s => s.id !== idSocio);
    if (socio) {
      this.mockHistorico.unshift(
        new HistoricoSwapModel(
          this.mockHistorico.length + 1,
          new Date().toISOString(),
          `Sócio ${socio.nome} removido.`,
          'usuario.logado',
          'INFO',
        )
      );
    }
    return of({ sucesso: true, retorno: null, mensagens: [] }).pipe(delay(300));
  }

  // ==================== Submissão ====================

  submeterParaValidacao(): Observable<ApiResponse<any>> {
    this.mockStatus = 'SUBMETIDO';
    this.mockHistorico.unshift(
      new HistoricoSwapModel(
        this.mockHistorico.length + 1,
        new Date().toISOString(),
        `Dados submetidos para validação SWAP/Bacen. Total de ${this.mockSocios.length} sócio(s).`,
        'usuario.logado',
        'SUBMISSAO',
      )
    );
    return of({ sucesso: true, retorno: null, mensagens: [] }).pipe(delay(800));
  }

  // ==================== Consulta Status SWAP ====================

  consultarStatusSwap(): Observable<ApiResponse<any>> {
    // Simula uma mudança de status ao consultar
    if (this.mockStatus === 'SUBMETIDO') {
      // Alterna entre ajuste e aprovado para demonstração
      const random = Math.random();
      if (random > 0.5) {
        this.mockStatus = 'APROVADO';
        this.mockHistorico.unshift(
          new HistoricoSwapModel(
            this.mockHistorico.length + 1,
            new Date().toISOString(),
            'SWAP aprovou a habilitação. Cliente liberado para operar.',
            'sistema',
            'APROVADO',
          )
        );
      } else {
        this.mockStatus = 'AJUSTE_SOLICITADO';
        this.mockHistorico.unshift(
          new HistoricoSwapModel(
            this.mockHistorico.length + 1,
            new Date().toISOString(),
            'SWAP solicitou ajustes nos dados cadastrais.',
            'sistema',
            'AJUSTE',
          )
        );
      }
    }
    return of({ sucesso: true, retorno: { status: this.mockStatus }, mensagens: [] }).pipe(delay(600));
  }

  // ==================== Histórico ====================

  buscarHistorico(): Observable<HistoricoSwapModel[]> {
    return of([...this.mockHistorico]).pipe(delay(300));
  }
}
